<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h2 style="color: white">Edit Tooth Chart</h2>
        <button class="close-btn" @click="closeModal">×</button>
      </div>
      <div v-if="loading">
        <loader />
      </div>

      <div v-if="!loading" class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- Tooth Identification -->
          <div class="tooth-card">
            <div class="tooth-icon">🦷</div>
            <div class="tooth-info">
              <h3 style="color: white">Tooth {{ form.toothNumber }}</h3>
              <p>{{ form.toothName }}</p>
            </div>
          </div>

          <!-- Main Form Grid -->
          <div class="form-grid">
            <!-- Tooth Status -->
            <div class="form-section">
              <h4><i class="fas fa-tooth"></i> Tooth Status</h4>
              <div class="status-grid">
                <button
                  v-for="status in toothStatuses"
                  :key="status.value"
                  type="button"
                  class="status-btn"
                  :class="{ active: form.status === status.value }"
                  @click="form.status = status.value"
                >
                  <i :class="status.icon"></i> {{ status.label }}
                </button>
              </div>
            </div>

            <!-- Restorations -->
            <div class="form-section">
              <h4><i class="fas fa-crown"></i> Restorations</h4>
              <div class="checkbox-grid">
                <label
                  v-for="type in restorationTypes"
                  :key="type.value"
                  class="checkbox-item"
                >
                  <input
                    type="checkbox"
                    v-model="form.restorations"
                    :value="type.value"
                  />
                  <span>{{ type.label }}</span>
                </label>
              </div>
            </div>

            <!-- Mobility -->
            <div class="form-section">
              <h4><i class="fas fa-ruler-vertical"></i> Mobility</h4>
              <div class="mobility-grid">
                <label
                  v-for="grade in mobilityGrades"
                  :key="grade"
                  class="mobility-item"
                  :class="{ active: form.mobility === grade }"
                >
                  <span>Grade {{ grade }}</span>
                  <input
                    type="radio"
                    v-model="form.mobility"
                    :value="grade"
                    name="mobility"
                  />
                </label>
              </div>
            </div>

            <!-- Furcation -->
            <div class="form-section">
              <h4><i class="fas fa-sitemap"></i> Furcation</h4>
              <select v-model="form.furcation" class="form-select">
                <option value="">Select Grade</option>
                <option value="I">Grade I</option>
                <option value="II">Grade II</option>
                <option value="III">Grade III</option>
                <option value="none">None</option>
              </select>
            </div>

            <!-- Periodontal Measurements -->
            <div class="form-section span-2">
              <h4><i class="fas fa-teeth-open"></i> Periodontal Charting</h4>
              <div class="measurements-grid">
                <div class="input-group">
                  <label>Pocket Depth (mm)*</label>
                  <input
                    type="number"
                    v-model="form.pocketDepth"
                    min="0"
                    max="15"
                    step="0.5"
                    placeholder="0.0"
                    required
                  />
                </div>
                <div class="input-group">
                  <label>Gingival Recession (mm)</label>
                  <input
                    type="number"
                    v-model="form.recession"
                    min="0"
                    max="10"
                    step="0.5"
                    placeholder="0.0"
                  />
                </div>
                <div class="input-group">
                  <label>Clinical Attachment Loss (mm)</label>
                  <input
                    type="number"
                    v-model="form.cal"
                    min="0"
                    max="20"
                    step="0.5"
                    placeholder="0.0"
                  />
                </div>
                <div class="input-group">
                  <label>Bleeding on Probing</label>
                  <div class="radio-group">
                    <label>
                      <input
                        type="radio"
                        v-model="form.bleeding"
                        :value="true"
                      />
                      Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        v-model="form.bleeding"
                        :value="false"
                      />
                      No
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plaque Index -->
            <div class="form-section">
              <h4><i class="fas fa-bacteria"></i> Plaque Index</h4>
              <div class="plaque-scale">
                <div
                  v-for="index in plaqueIndices"
                  :key="index.value"
                  class="scale-item"
                  :class="{ active: form.plaqueIndex === index.value }"
                  @click="form.plaqueIndex = index.value"
                >
                  <span class="score">{{ index.value }}</span>
                  <span class="desc">{{ index.label }}</span>
                </div>
              </div>
              <select v-model="form.plaqueIndex" class="form-select">
                <option value="">Select Index</option>
                <option
                  v-for="index in plaqueIndices"
                  :key="index.value"
                  :value="index.value"
                >
                  {{ index.value }} - {{ index.label }}
                </option>
              </select>
            </div>

            <!-- Additional Notes -->
            <div class="form-section span-2">
              <h4><i class="fas fa-sticky-note"></i> Clinical Notes</h4>
              <textarea
                v-model="form.notes"
                class="notes-textarea"
                placeholder="Enter any additional clinical observations or notes..."
                rows="3"
              ></textarea>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <div v-if="error" class="error-message">
              <i class="fas fa-exclamation-circle"></i> {{ error }}
            </div>
            <div v-if="success" class="success-message">
              <i class="fas fa-check-circle"></i> {{ success }}
            </div>
            <div class="action-buttons">
              <button
                type="button"
                class="btn-secondary"
                @click="closeModal"
                :disabled="loading"
              >
                Cancel
              </button>
              <button type="submit" class="btn-primary" :disabled="loading">
                <span v-if="loading">
                  <i class="fas fa-spinner fa-spin"></i> Saving...
                </span>
                <span v-else> <i class="fas fa-save"></i> Save Changes </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import loader from "../dialysis/loader.vue";
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "IndividualToothChartInput",
  props: {
    toothId: {
      type: [String, Number],
      required: true,
    },
    visit_id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      success: null,

      // Form state
      form: {
        toothNumber: "",
        toothName: "",
        status: "",
        restorations: [],
        mobility: null,
        furcation: "",
        pocketDepth: "",
        recession: "",
        cal: "",
        bleeding: null,
        plaqueIndex: "",
        notes: "",
        dateExamined: new Date().toISOString().split("T")[0],
      },

      // Static data
      toothStatuses: [
        { value: "sound", label: "Sound", icon: "fas fa-check-circle" },
        { value: "caries", label: "Caries", icon: "fas fa-biohazard" },
        { value: "filled", label: "Filled", icon: "fas fa-fill-drip" },
        { value: "missing", label: "Missing", icon: "fas fa-times-circle" },
        { value: "fractured", label: "Fractured", icon: "fas fa-cracked" },
        { value: "impacted", label: "Impacted", icon: "fas fa-ban" },
        { value: "root_stump", label: "Root Stump", icon: "fas fa-root" },
      ],

      restorationTypes: [
        { value: "amalgam", label: "Amalgam" },
        { value: "composite", label: "Composite" },
        { value: "crown", label: "Crown" },
        { value: "bridge", label: "Bridge" },
        { value: "veneer", label: "Veneer" },
        { value: "inlay_onlay", label: "Inlay/Onlay" },
        { value: "post_core", label: "Post & Core" },
      ],

      mobilityGrades: [1, 2, 3],

      plaqueIndices: [
        { value: 0, label: "No plaque" },
        { value: 1, label: "Plaque at gingival margin" },
        { value: 2, label: "Moderate plaque" },
        { value: 3, label: "Abundant plaque" },
      ],
    };
  },
  components: {
    loader,
  },

  // watch: {
  //   toothId: {
  //     immediate: true,
  //     handler(newToothId) {
  //       if (newToothId) {
  //         this.get_tooth_data();
  //       }
  //     },
  //   },
  // },

  methods: {
    async get_tooth_data() {
      try {
        this.loading = true;
        console.log(
          "Fetching data for tooth:",
          this.toothId,
          "visit:",
          this.visit_id
        );

        // You might need to adjust the API endpoint based on your backend
        // Option 1: If your API expects query parameters
        const url = `getPatientToothChart?visit_id=${this.visit_id}&tooth_id=${this.toothId}`;
        const res = await generalApiRequest(url, "GET", {}, 1);

        console.log("API Response:", res);

        // Check if response has data
        if (res && res.data) {
          const toothData = res.data;
          this.form = {
            toothNumber:
              toothData.fdi_iso_number || toothData.tooth_number || "",
            toothName: toothData.tooth_name || "",
            status: toothData.tooth_status || "",
            restorations: toothData.restorations || [],
            mobility: toothData.mobility_grading || null,
            furcation: toothData.furcation_involvement || "",
            pocketDepth: toothData.pocket_depths || "",
            recession: toothData.recession || "",
            cal: toothData.cal || "",
            bleeding:
              toothData.is_bleeding_on_probing == 1
                ? true
                : toothData.is_bleeding_on_probing == 0
                ? false
                : null,
            plaqueIndex: toothData.plaque_index || "",
            notes: toothData.other_notes || "",
            dateExamined:
              toothData.date_examined || new Date().toISOString().split("T")[0],
          };

          console.log("Form loaded:", this.form);
        } else {
          console.warn("No tooth data found in response");
          // Set default values based on toothId
          this.setDefaultToothInfo();
        }
      } catch (error) {
        console.error("Error fetching tooth data:", error);
        this.error = "Failed to load tooth data";
        // Set default values
        this.setDefaultToothInfo();
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      try {
        this.loading = true;
        this.error = null;
        this.success = null;

        // Validate required fields
        if (!this.form.status) {
          throw new Error("Please select a tooth status");
        }

        if (!this.form.pocketDepth && this.form.pocketDepth !== 0) {
          throw new Error("Pocket depth is required");
        }

        // Prepare payload for API
        const payload = {
          tooth_id: this.toothId,
          visit_id: this.visit_id,
          tooth_number: this.form.toothNumber,
          tooth_name: this.form.toothName,
          tooth_status: this.form.status,
          restorations: this.form.restorations,
          mobility_grading: this.form.mobility,
          furcation_involvement: this.form.furcation,
          pocket_depths: parseFloat(this.form.pocketDepth),
          recession: this.form.recession
            ? parseFloat(this.form.recession)
            : null,
          cal: this.form.cal ? parseFloat(this.form.cal) : null,
          is_bleeding_on_probing: this.form.bleeding,
          plaque_index: this.form.plaqueIndex
            ? parseInt(this.form.plaqueIndex)
            : null,
          other_notes: this.form.notes,
          date_examined: this.form.dateExamined,
        };

        console.log("Submitting payload:", payload);

        // Make API call
        const response = await generalApiRequest(
          "saveToothChart",
          "POST",
          payload,
          1
        );

        if (response.success) {
          this.success = "Tooth chart saved successfully!";

          // Emit success event to parent
          this.$emit("saved", payload);

          // Close modal after 2 seconds
          setTimeout(() => {
            this.closeModal();
          }, 2000);
        } else {
          throw new Error(response.message || "Failed to save tooth chart");
        }
      } catch (error) {
        this.error =
          error.message || "Failed to save tooth chart. Please try again.";
        console.error("Submit error:", error);
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit("close-tooth-input");
    },
  },
  mounted() {
    this.get_tooth_data();
  },
};
</script>

<style scoped>
/* Your existing styles remain the same */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: white;
  border-radius: 12px 12px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.modal-header h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}

.close-btn {
  background: #e74c3c;
  border: none;
  color: white;
  font-size: 24px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn:hover:not(:disabled) {
  background: #c0392b;
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 30px;
}

.tooth-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  border-radius: 8px;
  color: white;
  margin-bottom: 25px;
}

.tooth-icon {
  font-size: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
}

.tooth-info h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.tooth-info p {
  margin: 5px 0 0;
  opacity: 0.9;
  font-size: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 25px;
}

.form-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.form-section.span-2 {
  grid-column: span 2;
}

.form-section h4 {
  margin: 0 0 15px 0;
  color: #2d3748;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.status-btn {
  padding: 10px;
  border: 2px solid #cbd5e0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  transition: all 0.2s;
  font-size: 14px;
}

.status-btn:hover:not(:disabled) {
  border-color: #4299e1;
  background: #ebf8ff;
}

.status-btn.active {
  background: #4299e1;
  color: white;
  border-color: #3182ce;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.mobility-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.mobility-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px;
  background: white;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.mobility-item.active {
  border-color: #4299e1;
  background: #ebf8ff;
}

.mobility-item input[type="radio"] {
  width: 16px;
  height: 16px;
}

.form-select {
  width: 100%;
  padding: 10px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  background: white;
  font-size: 14px;
}

.measurements-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-group label {
  font-weight: 500;
  color: #4a5568;
  font-size: 13px;
}

.input-group label[for*="required"]::after {
  content: " *";
  color: #e53e3e;
}

.input-group input {
  padding: 10px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
}

.input-group input:focus {
  outline: none;
  border-color: #4299e1;
}

.radio-group {
  display: flex;
  gap: 15px;
  margin-top: 6px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-size: 14px;
}

.plaque-scale {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 15px;
}

.scale-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.scale-item.active {
  border-color: #4299e1;
  background: #ebf8ff;
}

.scale-item .score {
  font-weight: bold;
  color: #2d3748;
  font-size: 13px;
}

.scale-item .desc {
  font-size: 11px;
  color: #718096;
  margin-top: 3px;
  line-height: 1.2;
}

.notes-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
}

.notes-textarea:focus {
  outline: none;
  border-color: #4299e1;
}

.form-actions {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.error-message {
  background: #fed7d7;
  color: #9b2c2c;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.success-message {
  background: #c6f6d5;
  color: #276749;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-size: 14px;
  transition: all 0.2s;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-primary {
  background: #4299e1;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #3182ce;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover:not(:disabled) {
  background: #cbd5e0;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 900px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-section.span-2 {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .modal-content {
    max-height: 95vh;
  }

  .status-grid,
  .checkbox-grid,
  .measurements-grid {
    grid-template-columns: 1fr;
  }

  .mobility-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .plaque-scale {
    grid-template-columns: repeat(2, 1fr);
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .modal-body {
    padding: 20px;
  }

  .tooth-card {
    flex-direction: column;
    text-align: center;
    padding: 15px;
  }

  .mobility-grid,
  .plaque-scale {
    grid-template-columns: 1fr;
  }

  .modal-header {
    padding: 15px 20px;
  }
}
</style>
